import {AlertTriangle, UserCheck, Users} from "lucide-react";

import {Badge} from "@/components/ui/badge";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {cn} from "@/lib/utils";

import type {DashboardOfficers} from "../types";

type OfficersCardProps = {
  officers: DashboardOfficers;
};

/**
 * Officer signal card. Missed-shift block is intentionally given alert
 * styling (border + warning icon) — backend doc calls this out as a
 * service-failure indicator that should not blend in.
 */
export function OfficersCard({officers}: OfficersCardProps) {
  const activeNow = toCount(officers.active_now);
  const workedInPeriod = toCount(officers.worked_in_period);
  const missedCount = toCount(officers.missed_shift_in_period?.count);
  const missedItems = officers.missed_shift_in_period?.items ?? [];
  const hasMissed = missedCount > 0;

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-foreground text-sm font-semibold">Officers</CardTitle>
        <Badge variant="outline" className="font-normal">
          Company-wide
        </Badge>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Stat
            icon={UserCheck}
            label="Active now"
            value={activeNow}
            tone="positive"
          />
          <Stat icon={Users} label="Worked" value={workedInPeriod} />
        </div>

        <div
          className={cn(
            "flex flex-col gap-2 rounded-md border p-3",
            hasMissed
              ? "border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950"
              : "border-border bg-muted/30",
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <span
              className={cn(
                "flex items-center gap-2 text-sm font-medium",
                hasMissed
                  ? "text-rose-700 dark:text-rose-300"
                  : "text-muted-foreground",
              )}
            >
              <AlertTriangle className="size-4" />
              Officers with missed shifts
            </span>
            <span
              className={cn(
                "tabular-nums text-lg font-semibold",
                hasMissed
                  ? "text-rose-700 dark:text-rose-300"
                  : "text-muted-foreground",
              )}
            >
              {missedCount.toLocaleString()}
            </span>
          </div>

          {hasMissed && missedItems.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {missedItems.slice(0, 4).map((item, index) => (
                <li
                  key={`${item.user_id || "officer"}-${index}`}
                  className="text-foreground flex items-center justify-between text-xs"
                >
                  <span className="min-w-0 truncate">{item.name}</span>
                  <span className="text-rose-700 dark:text-rose-300 tabular-nums">
                    {item.missed_count}
                  </span>
                </li>
              ))}
              {missedItems.length > 4 ? (
                <li className="text-muted-foreground text-xs">
                  +{missedItems.length - 4} more
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  tone?: "positive";
}) {
  return (
    <div className="flex flex-col gap-1 rounded-md border p-3">
      <span className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
        <Icon className="size-3.5" />
        {label}
      </span>
      <span
        className={cn(
          "text-foreground text-xl font-semibold tabular-nums",
          tone === "positive" && value > 0 && "text-emerald-700 dark:text-emerald-400",
        )}
      >
        {value.toLocaleString()}
      </span>
    </div>
  );
}

function toCount(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value && typeof value === "object") {
    const maybeCount = (value as {count?: unknown}).count;
    if (typeof maybeCount === "number" && Number.isFinite(maybeCount)) return maybeCount;
  }
  return 0;
}
